import { useContext, useEffect, useState } from 'react';
import { useAuth0 } from "@auth0/auth0-react";

// MUI
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { CircularProgressbar } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";

// Icons
import LocationOn from '@mui/icons-material/LocationOn';
import LocationOff from '@mui/icons-material/LocationOff';
import Wifi from '@mui/icons-material/Wifi';
import WifiOff from '@mui/icons-material/WifiOff';
import Person from '@mui/icons-material/Person';
import Storage from '@mui/icons-material/Storage';
import { Check, Warning } from "@mui/icons-material";
import { green, orange, red } from "@mui/material/colors";

// Context
import AppContext from '../../context/appContext';

// Components
import DrawerHeaderBar from '../DrawerHeaderBar/DrawerHeaderBar';

// Helpers
import requestPermissions from '../../services/helpers/requestPermissions';
import { isStoragePersisted } from '../../services/helpers/storageUtils';

export default function StatusPage({ show, setShowStatus }) {
    const { currentUser, isOnline, storageData, setAlert } = useContext(AppContext);
    const { isAuthenticated, loginWithRedirect } = useAuth0();
    
    const [locationAccess, setLocationAccess] = useState(null);
    const [isPersisted, setIsPersisted] = useState(null);

    const handleClose = () => {
        setShowStatus(false);
    };

    // Geolocation permission check
    const geolocationPermissionCheck = async () => {
        try {
            let geolocationAccess = await requestPermissions('geolocation');
            if (geolocationAccess.state === 'prompt') {
                setLocationAccess(false);
            } else if (geolocationAccess.state === 'denied') {
                setLocationAccess(false);
            } else if (geolocationAccess.state === 'granted') {
                setLocationAccess(true);
            }
        } catch (error) {
            setLocationAccess(false);
        }
    };

    const promptGeo = () => {
        navigator.geolocation.getCurrentPosition(locationSuccess, locationError, { maximumAge: 1000, timeout: 60000 });
    };

    const locationSuccess = (position) => {
        setLocationAccess(true);
        setAlert({show: true, severity: 'success', message: `Geolocalización habilitada. Precisión actual: ${position.coords.accuracy.toFixed(2)} m`});
    };

    const locationError = (error) => {
        setLocationAccess(false);
        setAlert({show: true, severity: 'error', message: `Error al habilitar geolocalización.`});
    };

    // Storage persistence check
    useEffect(() => {
        async function checkPersisted() {
            try {
                const persisted = await isStoragePersisted();
                setIsPersisted(persisted);
            } catch (error) {
                console.error("Error al verificar persistencia de almacenamiento:", error);
                setIsPersisted(false);
            }
        }
        
        if (show) {
            checkPersisted();
            geolocationPermissionCheck();
            // Verificar periódicamente mientras el drawer está abierto
            const interval = setInterval(() => {
                checkPersisted();
                geolocationPermissionCheck();
            }, 2000);
            return () => clearInterval(interval);
        }
    }, [show]);

    const getStorageDonutChart = () => {
        // Asegurar que siempre se muestre el donut chart, incluso con valores por defecto
        const usagePct = storageData?.usagePct || 0;
        const severity = storageData?.colorCode || 'success';
        const color = severity === 'error' ? red[600] : severity === 'warning' ? orange[600] : green[500];
        
        return (
            <Box sx={{ width: "5rem", height: "5rem", position: "relative", display: "inline-flex" }}>
                <CircularProgressbar
                    text={`${usagePct.toFixed(0)}%`}
                    value={usagePct}
                    styles={{
                        path: {
                            stroke: color,
                            strokeLinecap: "butt",
                            transition: "stroke-dashoffset 0.5s ease 0s",
                            transformOrigin: "center center",
                        },
                        trail: {
                            stroke: "#d6d6d6",
                            strokeLinecap: "butt",
                            transformOrigin: "center center",
                        },
                        text: {
                            fontSize: "16px",
                            fill: "#000",
                        },
                        background: {
                            fill: "#3e98c7",
                        },
                    }}
                />
            </Box>
        );
    };

    return (
        <Drawer
            PaperProps={{ sx: { borderRadius: "10px 10px 0 0" } }}
            anchor="bottom"
            open={show || false}
            onClose={handleClose}
        >
            <Stack
                direction={"column"}
                m={1}
                height={"100vh"}
            >
                <DrawerHeaderBar
                    title={"Estado"}
                    handleClose={handleClose}
                />
                <Stack
                    height={"100%"}
                    sx={{ overflowY: 'auto' }}
                >
                    <List>
                        {/* Item: Perfil del Usuario */}
                        <ListItem>
                            <ListItemIcon>
                                <Person color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Avatar 
                                            alt={currentUser?.userName || 'Usuario'} 
                                            src={currentUser?.picture} 
                                            sx={{ width: 32, height: 32 }} 
                                        />
                                        <Typography variant="body1">
                                            {currentUser?.userName || 'No disponible'}
                                        </Typography>
                                    </Stack>
                                }
                                primaryTypographyProps={{ component: 'div' }}
                                secondary={
                                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Sesión: {isAuthenticated ? 'Iniciada' : 'No iniciada'}
                                        </Typography>
                                        {!isAuthenticated && isOnline && (
                                            <Button 
                                                color='secondary' 
                                                variant="outlined"
                                                size="small"
                                                onClick={loginWithRedirect}
                                                sx={{ mt: 0.5, width: 'fit-content' }}
                                            >
                                                LOGIN
                                            </Button>
                                        )}
                                    </Stack>
                                }
                                secondaryTypographyProps={{ component: 'div' }}
                            />
                        </ListItem>
                        <Divider />

                        {/* Item: Almacenamiento */}
                        <ListItem>
                            <ListItemIcon>
                                <Storage color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="Almacenamiento"
                                secondary={
                                    <Stack spacing={1} sx={{ mt: 1 }}>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            {getStorageDonutChart()}
                                            <Stack spacing={0.5} flex={1}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Typography variant="caption">Persistente</Typography>
                                                    {isPersisted === null ? (
                                                        <Typography variant="caption" color="text.secondary">Verificando...</Typography>
                                                    ) : isPersisted ? (
                                                        <Check fontSize={"14px"} color={"success"} />
                                                    ) : (
                                                        <Warning fontSize={"14px"} color={"warning"} />
                                                    )}
                                                </Stack>
                                                {storageData && (
                                                    <>
                                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                            <Typography variant="caption" color="text.secondary">
                                                                {storageData.usageMb?.toFixed(1) || '0'} MB / {storageData.quotaMb?.toFixed(1) || '0'} MB
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {storageData.usagePct?.toFixed(1) || '0'}%
                                                            </Typography>
                                                        </Stack>
                                                        <LinearProgress 
                                                            value={storageData.usagePct || 0} 
                                                            variant='determinate' 
                                                            color={storageData.colorCode}
                                                            sx={{ height: 6, borderRadius: 1 }}
                                                        />
                                                        <Typography variant="caption" color="text.secondary">
                                                            Disponible: {storageData.availableMb?.toFixed(1) || '0'} MB
                                                        </Typography>
                                                    </>
                                                )}
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                }
                                secondaryTypographyProps={{ component: 'div' }}
                            />
                        </ListItem>
                        <Divider />

                        {/* Item: Geolocalización */}
                        <ListItem>
                            <ListItemIcon>
                                {locationAccess === null ? (
                                    <LocationOff color="disabled" />
                                ) : locationAccess ? (
                                    <LocationOn color="primary" />
                                ) : (
                                    <LocationOff color="warning" />
                                )}
                            </ListItemIcon>
                            <ListItemText
                                primary="Geolocalización"
                                secondary={
                                    <Stack spacing={1} sx={{ mt: 1 }}>
                                        <Typography variant="body2" color={locationAccess ? "success.main" : "warning.main"}>
                                            {locationAccess === null 
                                                ? "Verificando..." 
                                                : locationAccess 
                                                    ? "Geolocalización habilitada" 
                                                    : "Geolocalización no habilitada"}
                                        </Typography>
                                        <Button 
                                            variant="outlined" 
                                            size="small"
                                            onClick={promptGeo}
                                            startIcon={locationAccess ? <LocationOn /> : <LocationOff />}
                                            sx={{ width: 'fit-content' }}
                                        >
                                            {locationAccess ? 'Actualizar ubicación' : 'Habilitar geolocalización'}
                                        </Button>
                                    </Stack>
                                }
                                secondaryTypographyProps={{ component: 'div' }}
                            />
                        </ListItem>
                        <Divider />

                        {/* Item: Conectividad */}
                        <ListItem>
                            <ListItemIcon>
                                {isOnline ? (
                                    <Wifi color="primary" />
                                ) : (
                                    <WifiOff color="warning" />
                                )}
                            </ListItemIcon>
                            <ListItemText
                                primary="Conectividad"
                                secondary={
                                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                                        <Typography variant="body2" color={isOnline ? "success.main" : "warning.main"}>
                                            {isOnline ? "Dispositivo online" : "Dispositivo offline"}
                                        </Typography>
                                        {!isOnline && (
                                            <Typography variant="caption" color="text.secondary">
                                                Puede seguir trabajando de manera offline. Deberá tener conectividad para sincronizar datos al servidor o descargar mapas
                                            </Typography>
                                        )}
                                    </Stack>
                                }
                                secondaryTypographyProps={{ component: 'div' }}
                            />
                        </ListItem>
                    </List>

                    {/* Botón de cerrar */}
                    <Box p={2}>
                        <Button 
                            variant="contained" 
                            fullWidth 
                            onClick={handleClose}
                        >
                            CERRAR
                        </Button>
                    </Box>
                </Stack>
            </Stack>
        </Drawer>
    );
}
